<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="store.loading" />

  <div v-else class="container-fluid my-3">
    <div class="section-header mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
      <span class="section-label">طرق الدفع</span>
      <button class="btn PrimaryButton" @click="openCreate">
        <i class="pi pi-plus me-1"></i> إضافة طريقة دفع
      </button>
    </div>

    <div v-if="!store.methods.length" class="text-center py-5 text-muted">
      <i class="pi pi-credit-card" style="font-size:2.5rem;"></i>
      <p class="mt-2">لا توجد طرق دفع بعد.</p>
    </div>

    <div v-else class="card">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-bordered align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th style="width:64px">الشعار</th>
                <th>الاسم</th>
                <th>الحساب</th>
                <th>IBAN</th>
                <th style="width:70px">QR</th>
                <th style="width:90px">الحالة</th>
                <th style="width:150px">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in store.methods" :key="m.id" :class="{ 'table-secondary': !m.is_active }">
                <td>
                  <img v-if="m.image_link" :src="m.image_link" class="pm-thumb" alt="" />
                  <span v-else class="pm-thumb pm-thumb--empty"><i class="pi pi-wallet"></i></span>
                </td>
                <td class="fw-bold">{{ m.name }}</td>
                <td>
                  <div>{{ m.account_name || '—' }}</div>
                  <small class="text-muted">{{ m.account_number || '' }}</small>
                </td>
                <td><small>{{ m.iban || '—' }}</small></td>
                <td>
                  <img v-if="m.qr_image_link" :src="m.qr_image_link" class="pm-qr" alt="" @click="viewImage(m.qr_image_link)" />
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <button class="btn btn-sm" :class="m.is_active ? 'btn-success' : 'btn-outline-secondary'" @click="toggle(m)">
                    {{ m.is_active ? 'مفعّل' : 'معطّل' }}
                  </button>
                </td>
                <td>
                  <button class="btn btn-sm btn-primary me-1" @click="openEdit(m)" title="تعديل"><i class="pi pi-pencil"></i></button>
                  <button class="btn btn-sm btn-danger" @click="remove(m)" title="حذف"><i class="pi pi-trash"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <Dialog v-model:visible="dialogOpen" modal :style="{ width: '44rem' }" :breakpoints="{ '960px': '95vw' }"
          :header="editing ? 'تعديل طريقة دفع' : 'إضافة طريقة دفع'">
    <div class="row g-3">
      <div class="col-md-6">
        <label class="form-label">الاسم <span class="text-danger">*</span></label>
        <input type="text" class="form-control" v-model="form.name" placeholder="مثال: إنستا باي" />
      </div>
      <div class="col-md-3">
        <label class="form-label">الترتيب</label>
        <input type="number" class="form-control" v-model="form.sort_order" min="0" />
      </div>
      <div class="col-md-3 d-flex align-items-end gap-2">
        <InputSwitch v-model="form.is_active" inputId="pm_active" />
        <label for="pm_active" class="mb-0">مفعّل</label>
      </div>
      <div class="col-md-6">
        <label class="form-label">اسم الحساب</label>
        <input type="text" class="form-control" v-model="form.account_name" />
      </div>
      <div class="col-md-6">
        <label class="form-label">رقم الحساب / المحفظة</label>
        <input type="text" class="form-control" v-model="form.account_number" />
      </div>
      <div class="col-md-6">
        <label class="form-label">IBAN</label>
        <input type="text" class="form-control" v-model="form.iban" />
      </div>
      <div class="col-12">
        <label class="form-label">التعليمات</label>
        <textarea class="form-control" rows="2" v-model="form.instructions"></textarea>
      </div>
      <div class="col-md-6">
        <label class="form-label">الشعار</label>
        <input type="file" class="form-control" accept="image/*" @change="onImage($event, 'image')" />
        <img v-if="preview.image" :src="preview.image" class="mt-2 rounded" style="max-height:64px" alt="" />
      </div>
      <div class="col-md-6">
        <label class="form-label">صورة QR</label>
        <input type="file" class="form-control" accept="image/*" @change="onImage($event, 'qr_image')" />
        <img v-if="preview.qr_image" :src="preview.qr_image" class="mt-2 rounded" style="max-height:64px" alt="" />
      </div>
    </div>
    <template #footer>
      <button class="btn btn-secondary" @click="dialogOpen = false">إلغاء</button>
      <button class="btn PrimaryButton" :disabled="store.saving || !form.name.trim()" @click="save">
        <i class="pi pi-check me-1"></i>{{ store.saving ? 'جارٍ الحفظ...' : 'حفظ' }}
      </button>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import InputSwitch from 'primevue/inputswitch';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { usePaymentMethodStore } from '@/stores/usePaymentMethodStore.js';
import { apiError } from '@/composables/subscriptionMeta.js';

function emptyForm() {
  return { name: '', account_name: '', account_number: '', iban: '', instructions: '', sort_order: 0, is_active: true };
}

export default {
  name: 'PaymentMethodsIndex',
  components: { Dialog, InputSwitch, Breadcrumb, Loading },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'طرق الدفع', path: '/payment-methods' }],
      dialogOpen: false,
      editing: null,
      form: emptyForm(),
      files: { image: null, qr_image: null },
      preview: { image: null, qr_image: null },
    };
  },
  computed: {
    store() { return usePaymentMethodStore(); },
  },
  methods: {
    openCreate() {
      this.editing = null;
      this.form = emptyForm();
      this.files = { image: null, qr_image: null };
      this.preview = { image: null, qr_image: null };
      this.dialogOpen = true;
    },
    openEdit(m) {
      this.editing = m;
      this.form = {
        name: m.name ?? '', account_name: m.account_name ?? '', account_number: m.account_number ?? '',
        iban: m.iban ?? '', instructions: m.instructions ?? '', sort_order: m.sort_order ?? 0, is_active: !!m.is_active,
      };
      this.files = { image: null, qr_image: null };
      this.preview = { image: m.image_link, qr_image: m.qr_image_link };
      this.dialogOpen = true;
    },
    onImage(event, field) {
      const file = event.target.files[0];
      if (!file) return;
      this.files[field] = file;
      this.preview[field] = URL.createObjectURL(file);
    },
    buildFormData() {
      const fd = new FormData();
      const f = this.form;
      fd.append('name', f.name.trim());
      fd.append('account_name', f.account_name ?? '');
      fd.append('account_number', f.account_number ?? '');
      fd.append('iban', f.iban ?? '');
      fd.append('instructions', f.instructions ?? '');
      fd.append('sort_order', f.sort_order || 0);
      fd.append('is_active', f.is_active ? 1 : 0);
      if (this.files.image) fd.append('image', this.files.image);
      if (this.files.qr_image) fd.append('qr_image', this.files.qr_image);
      return fd;
    },
    async save() {
      try {
        const fd = this.buildFormData();
        if (this.editing) await this.store.updateMethod(this.editing.id, fd);
        else await this.store.createMethod(fd);
        this.dialogOpen = false;
        Swal.fire({ icon: 'success', title: 'تم الحفظ', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
    async toggle(m) {
      try { await this.store.toggle(m.id); }
      catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false }); }
    },
    async remove(m) {
      const res = await Swal.fire({
        title: 'حذف طريقة الدفع', html: `سيتم حذف <strong>${m.name}</strong> نهائياً.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.deleteMethod(m.id);
        Swal.fire({ icon: 'success', title: 'تم الحذف', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
    viewImage(src) {
      Swal.fire({ imageUrl: src, imageAlt: 'QR', showConfirmButton: false, showCloseButton: true });
    },
  },
  mounted() {
    this.store.fetchMethods();
  },
};
</script>

<style scoped>
.pm-thumb {
  width: 48px; height: 48px; border-radius: 8px; object-fit: cover; display: inline-flex;
  align-items: center; justify-content: center;
}
.pm-thumb--empty { background: #f1f5f9; color: #94a3b8; }
.pm-qr { width: 42px; height: 42px; object-fit: cover; cursor: pointer; border-radius: 6px; }
</style>
